import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch, API_BASE, getToken } from '../../services/apiClient';
import { unitPer } from './AdminBidDetail';   // C = per 100, M = per 1,000

// Line items for an OLDER job, from its old quote. Shown on the job's Line Items tile
// only while the job has none. Pick the quote from the job's Documents (or add it
// here), the existing old-quote reader pulls the priced lines, you check them against
// the quote, and they become the job's line items — priced exactly as quoted, which is
// what pay applications then bill against.

const READABLE = /\.(pdf|xlsx|xls|xlsm|csv|png|jpe?g|webp)$/i;
const money = n => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const num = v => { const n = parseFloat(String(v).replace(/[^0-9.-]/g, '')); return Number.isFinite(n) ? n : 0; };
const btn = (bg, fg, border) => ({ background: bg, color: fg, border: border || 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' });
const cell = { border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' };

export default function QuoteImport({ bidId, projectId, onImported }) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState('');          // '' | 'upload' | 'read' | 'save'
  const [err, setErr] = useState('');
  const [source, setSource] = useState(null);    // { id, title }
  const [rows, setRows] = useState(null);        // the lines being reviewed
  const [quoteTotal, setQuoteTotal] = useState('');

  const loadDocs = useCallback(() => apiFetch(`/projects/${projectId}/documents`)
    .then(d => setDocs((Array.isArray(d) ? d : []).filter(x => READABLE.test(x.original_name || x.filename || x.title || ''))))
    .catch(() => setDocs([])), [projectId]);
  useEffect(() => { if (open && !rows) loadDocs(); }, [open, rows, loadDocs]);

  async function read(doc) {
    setBusy('read'); setErr(''); setSource({ id: doc.id, title: doc.title || doc.original_name });
    try {
      // Documents that are really bid attachments carry an "att:" prefix in the ledger.
      const attId = String(doc.id).startsWith('att:') ? String(doc.id).slice(4) : doc.id;
      const r = await apiFetch(`/bids/${bidId}/attachments/${attId}/import-quote`, { method: 'POST' });
      const items = (r.items || []).map((it, i) => ({ key: i, description: it.description || '', quantity: it.quantity || 1, unit: it.unit || 'EA', unit_price: it.dje_standard || 0 }));
      if (!items.length) { setErr('No priced lines were found in that file. Is it the quote with the line items on it?'); setSource(null); }
      else setRows(items);
    } catch (e) { setErr(e?.error || e?.message || 'That file could not be read.'); setSource(null); }
    setBusy('');
  }

  async function upload(file) {
    if (!file) return;
    setBusy('upload'); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('project_id', projectId); fd.append('bid_id', bidId);
      fd.append('doc_type', 'quote'); fd.append('title', file.name);
      const res = await fetch(`${API_BASE}/documents`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.id) throw new Error(d.error || 'The upload did not go through.');
      window.dispatchEvent(new Event('titan:documents-changed'));
      setBusy('');
      await read({ id: d.id, title: d.title || file.name });
    } catch (e) { setErr(e.message); setBusy(''); }
  }

  async function save() {
    const clean = rows.filter(r => r.description.trim());
    if (!clean.length) { setErr('There are no lines to save.'); return; }
    setBusy('save'); setErr('');
    try {
      const r = await apiFetch(`/bids/${bidId}/items/import`, { method: 'POST', body: JSON.stringify({
        source_name: source?.title || '',
        items: clean.map(x => ({ description: x.description.trim(), quantity: num(x.quantity) || 1, unit: x.unit.trim(), unit_price: num(x.unit_price) })),
      }) });
      setOpen(false); setRows(null);
      onImported && onImported(r.items || []);
    } catch (e) { setErr(e?.error || e?.message || 'That did not save.'); }
    setBusy('');
  }

  const edit = (key, field, value) => setRows(rs => rs.map(r => (r.key === key ? { ...r, [field]: value } : r)));
  const total = (rows || []).reduce((s, r) => s + num(r.quantity) * num(r.unit_price) / unitPer(r.unit), 0);
  const want = num(quoteTotal);
  const diff = Math.round((total - want) * 100) / 100;

  return (
    <>
      <button onClick={() => { setOpen(true); setErr(''); }}
        style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', background: '#eff6ff', border: '1.5px dashed #93c5fd', borderRadius: 12, padding: '14px 16px', cursor: 'pointer' }}>
        <span style={{ fontSize: 26 }}>📄</span>
        <span>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 800, color: '#1e3a5f' }}>Import line items from the old quote</span>
          <span style={{ display: 'block', fontSize: 12.5, color: '#4b5563', marginTop: 2 }}>For a job that came over without them. Reads the quote, you check the lines, and they come in priced exactly as quoted.</span>
        </span>
      </button>

      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: rows ? 980 : 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>{rows ? 'Check the lines against the quote' : 'Import line items from the old quote'}</div>
                {rows && source && <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>Read from “{source.title}”. Fix anything that’s off — nothing is saved until you press Save.</div>}
              </div>
              <button onClick={() => { if (busy) return; setOpen(false); setRows(null); setSource(null); }} style={{ background: 'none', border: 'none', fontSize: 24, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</div>}

              {!rows && busy === 'read' && <div style={{ textAlign: 'center', padding: '40px 0', color: '#374151', fontSize: 14 }}>Reading “{source?.title}”…<div style={{ fontSize: 12.5, color: '#9ca3af', marginTop: 6 }}>A long quote can take a minute.</div></div>}
              {!rows && busy === 'upload' && <div style={{ textAlign: 'center', padding: '40px 0', color: '#374151', fontSize: 14 }}>Filing it in the job’s Documents…</div>}

              {!rows && !busy && (<>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Pick the quote from this job’s Documents</div>
                {docs === null && <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>}
                {docs && docs.length === 0 && <div style={{ color: '#6b7280', fontSize: 13, background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8, padding: 14 }}>Nothing readable is in this job’s Documents yet (PDF, Excel, CSV or a photo). Add the quote below — it gets filed under Quotes.</div>}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(docs || []).slice(0, 60).map(d => (
                    <button key={d.id} onClick={() => read(d)} style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', background: '#fff', border: `1px solid ${d.doc_type === 'quote' ? '#93c5fd' : '#e5e7eb'}`, borderRadius: 8, padding: '9px 12px', cursor: 'pointer' }}>
                      <span style={{ fontSize: 16 }}>{/\.pdf$/i.test(d.original_name || d.title || '') ? '📕' : /\.(xlsx?|xlsm|csv)$/i.test(d.original_name || d.title || '') ? '📗' : '🖼️'}</span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title || d.original_name}</span>
                      {d.doc_type === 'quote' && <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', borderRadius: 6, padding: '2px 8px' }}>Quote</span>}
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>Read it ›</span>
                    </button>
                  ))}
                </div>
                <label style={{ display: 'block', marginTop: 14, textAlign: 'center', border: '1.5px dashed #d1d5db', borderRadius: 10, padding: 14, fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>
                  ＋ Or add the quote from this computer
                  <input type="file" accept=".pdf,.xlsx,.xls,.xlsm,.csv,image/*" style={{ display: 'none' }} onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
              </>)}

              {rows && (<>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px', minWidth: 720 }}>
                    <thead><tr style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'left' }}>
                      <th style={{ width: 30 }}>#</th><th>Description</th><th style={{ width: 90 }}>Qty</th><th style={{ width: 80 }}>Unit</th><th style={{ width: 130 }}>Unit price</th><th style={{ width: 120, textAlign: 'right' }}>Total</th><th style={{ width: 30 }} />
                    </tr></thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.key}>
                          <td style={{ fontSize: 12, color: '#9ca3af' }}>{i + 1}</td>
                          <td><input style={cell} value={r.description} onChange={e => edit(r.key, 'description', e.target.value)} /></td>
                          <td><input style={{ ...cell, textAlign: 'right' }} inputMode="decimal" value={r.quantity} onChange={e => edit(r.key, 'quantity', e.target.value)} /></td>
                          <td><input style={cell} value={r.unit} onChange={e => edit(r.key, 'unit', e.target.value)} /></td>
                          <td><input style={{ ...cell, textAlign: 'right', borderColor: num(r.unit_price) > 0 ? '#e5e7eb' : '#fca5a5', background: num(r.unit_price) > 0 ? '#fff' : '#fef2f2' }} inputMode="decimal" value={r.unit_price} onChange={e => edit(r.key, 'unit_price', e.target.value)} /></td>
                          <td style={{ textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(num(r.quantity) * num(r.unit_price) / unitPer(r.unit))}</td>
                          <td><button title="Drop this line" onClick={() => setRows(rs => rs.filter(x => x.key !== r.key))} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 16, cursor: 'pointer' }}>×</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button onClick={() => setRows(rs => [...rs, { key: Date.now(), description: '', quantity: 1, unit: 'EA', unit_price: 0 }])} style={{ marginTop: 6, background: 'none', border: '1px dashed #d1d5db', borderRadius: 7, padding: '6px 12px', color: '#374151', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>＋ Add a line it missed</button>
                {rows.some(r => !(num(r.unit_price) > 0)) && <div style={{ fontSize: 12.5, color: '#b45309', marginTop: 8 }}>Lines in red came back with no price — type it in from the quote, or drop the line.</div>}
              </>)}
            </div>

            {rows && (
              <div style={{ padding: '14px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', background: '#fafafa' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{rows.length} lines add up to</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>{money(total)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>The quote’s total says</div>
                  <input value={quoteTotal} onChange={e => setQuoteTotal(e.target.value)} placeholder="type it to compare" inputMode="decimal" style={{ ...cell, width: 170, fontSize: 15, fontWeight: 700 }} />
                </div>
                {quoteTotal !== '' && (
                  <div style={{ fontSize: 13, fontWeight: 800, color: diff === 0 ? '#15803d' : '#b91c1c' }}>
                    {diff === 0 ? '✓ Matches the quote' : `${diff > 0 ? 'Over' : 'Short'} by ${money(Math.abs(diff))} — a line is off (tax isn’t a line)`}
                  </div>
                )}
                <span style={{ flex: 1 }} />
                <button disabled={!!busy} onClick={() => { setRows(null); setSource(null); setQuoteTotal(''); }} style={btn('#fff', '#6b7280', '1.5px solid #d1d5db')}>Pick a different file</button>
                <button disabled={!!busy} onClick={save} style={btn('#1e3a5f', '#fff')}>{busy === 'save' ? 'Saving…' : `Save ${rows.filter(r => r.description.trim()).length} line items`}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
